import { Button } from "@/components/ui/baseUi/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/baseUi/carousel"
import { Text } from "@/components/ui/baseUi/text"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { cn } from "@/modules/lib/utils"
import { FileUploadImage } from "@/modules/ui/components/fileUpload/fileUploadImage"
import { word } from "@modules/core/utils/i18n"
import { ImagePlus, TrashIcon } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

type HUGalleryFormProps = {
  images: MediaItemFormType[]
  onChange: (images: MediaItemFormType[]) => void
  maxImages?: number
  emptyLabel?: string
  addLabel?: string
  className?: string
}

export function HUGalleryForm({
  images,
  onChange,
  maxImages = 5,
  emptyLabel = word("file_upload.gallery.empty"),
  addLabel = word("file_upload.gallery.add"),
  className,
}: HUGalleryFormProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [currentIndex, setCurrentIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!api) return
    setCurrentIndex(api.selectedScrollSnap())
  }, [api])

  useEffect(() => {
    if (!api) return
    api.on("select", onSelect)
    api.on("reInit", onSelect)
    return () => {
      api.off("select", onSelect)
      api.off("reInit", onSelect)
    }
  }, [api, onSelect])

  function handleAdd(media?: MediaItemFormType) {
    if (!media) return
    const next = [...images, media]
    onChange(next)
    // scroll to the newly added item
    setTimeout(() => api?.scrollTo(next.length - 1), 50)
  }

  function handleDelete(index: number) {
    const next = images.filter((_, i) => i !== index)
    onChange(next)
    if (currentIndex >= next.length && currentIndex > 0) {
      api?.scrollTo(next.length - 1)
    }
  }

  const canAdd = images.length < maxImages

  if (images.length === 0) {
    return (
      <FileUploadImage setImage={handleAdd}>
        <div className="flex flex-row items-center justify-center gap-2 rounded-xl border border-dashed border-interactive-border-neutral-default bg-surface-background-default px-8 py-20 transition-colors hover:bg-surface-background-secondary">
          <ImagePlus className="size-5 text-textOnSurface-subtle" />
          <Text fontStyle="BM" className="text-textOnSurface-subtle">
            {emptyLabel}
          </Text>
        </div>
      </FileUploadImage>
    )
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <Carousel setApi={setApi} opts={{ align: "start", loop: false }}>
        <CarouselContent>
          {images.map((item, index) => {
            const src = item.file ? URL.createObjectURL(item.file) : (item.url ?? undefined)
            return (
              <CarouselItem key={index}>
                <div className="relative overflow-hidden rounded-2xl">
                  {src && <img src={src} alt="" className="h-48 w-full object-cover" />}
                  <Button
                    variant="outline"
                    size="iconMd"
                    onClick={() => {
                      handleDelete(index)
                    }}
                    className="absolute top-2 right-2"
                  >
                    <TrashIcon className="size-5 text-textOnSurface-subtle" />
                  </Button>
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        {images.length > 1 && (
          <>
            <CarouselPrevious className="-left-3" />
            <CarouselNext className="-right-3" />
          </>
        )}
      </Carousel>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-1.5">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => api?.scrollTo(i)}
              className={cn(
                "size-2 rounded-full transition-colors",
                i === currentIndex
                  ? "bg-interactive-background-primary-default"
                  : "bg-interactive-background-neutral-faded-highlighted",
              )}
            />
          ))}
        </div>
      )}

      {/* Add more — pill button */}
      {canAdd && (
        <FileUploadImage setImage={handleAdd}>
          <div className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-3xl border border-interactive-border-neutral-default bg-surface-background-default px-4 py-2 transition-colors hover:bg-surface-background-secondary">
            <ImagePlus className="size-5 text-textOnSurface-subtle" />
            <Text fontStyle="BM" className="text-textOnSurface-subtle">
              {addLabel}
            </Text>
          </div>
        </FileUploadImage>
      )}
    </div>
  )
}
